import { createCsvBlob, repairCsvText } from './csvEncoding'

const formatValue = (val, t) => {

  if (val === null || val === undefined || val === '') return ''

  if (typeof val === 'boolean') {
    return val ? t('dropdown_yes') : t('dropdown_no')
  }

  if (typeof val === 'number') return val.toString()
  if (typeof val === 'object') return JSON.stringify(val)

  const v = String(val).trim()

  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    try {
      const d = new Date(v)

      const day = String(d.getDate()).padStart(2,'0')
      const month = String(d.getMonth()+1).padStart(2,'0')
      const year = d.getFullYear()

      return `${day}.${month}.${year}`
    } catch {
      return v
    }
  }

  return v
}

const pad = (n) => String(n).padStart(2, '0')

const fileStamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const csvEscape = (value, t) => `"${repairCsvText(formatValue(value, t))}"`

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

const loadPdfFont = async (doc, url, fileName, fontName, fontStyle) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`PDF font not found: ${url}`)
  }

  const buffer = await response.arrayBuffer()
  const base64 = arrayBufferToBase64(buffer)

  doc.addFileToVFS(fileName, base64)
  doc.addFont(fileName, fontName, fontStyle)
}

const ensureVehiclePdfFonts = async (doc) => {
  await loadPdfFont(
    doc,
    '/fonts/Roboto-Regular.ttf',
    'Roboto-Regular.ttf',
    'Roboto',
    'normal'
  )

  await loadPdfFont(
    doc,
    '/fonts/Roboto-Bold.ttf',
    'Roboto-Bold.ttf',
    'Roboto',
    'bold'
  )
}

const translateVehicleHeader = (h, t) => {

  const map = {
    name: 'vehicle_name',
    licensePlate: 'vehicle_license_plate',
    vin: 'vehicle_vin',
    brand: 'vehicle_brand',
    model: 'vehicle_model',
    variantType: 'vehicle_type',
    vehicleType: 'vehicle_class',
    usageType: 'vehicle_usage_type',
    category: 'vehicle_category',
    fuelType: 'vehicle_fuel_type',
    engineCapacity: 'vehicle_engine_capacity',
    power: 'vehicle_power',
    transmission: 'vehicle_transmission',
    color: 'vehicle_color',
    emissionsCo2: 'vehicle_emissions_co2',
    consumption: 'vehicle_consumption',
    emissionClass: 'vehicle_emission_class',
    year: 'vehicle_year',
    stkDate: 'vehicle_stk_date',
    ekDate: 'vehicle_ek_date',
    serviceDate: 'vehicle_service_date',
    tiresAllSeason: 'vehicle_tires_all_season',
    startDate: 'vehicle_start_date',
    pzpDate: 'vehicle_pzp_date',
    pzpFrequency: 'vehicle_pzp_frequency',
    cascoDate: 'vehicle_casco_date',
    cascoFrequency: 'vehicle_casco_frequency',
    registrationCountry: 'vehicle_registration_country',
    note: 'vehicle_note'
  }

  const key = map[h]

  if (key) return t(key)

  return h
}

export const exportVehiclesCSV = (records, headers, t) => {

  const header = headers.map(h => translateVehicleHeader(h, t)).join(';')

  const rows = records.map(r =>
    headers.map(h => csvEscape(r[h], t)).join(';')
  )

  const csv = [
    header,
    ...rows
  ].join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `vehicles_${fileStamp()}.csv`
  )
}

export const exportVehiclesXLS = async (records, headers, t) => {

  const XLSX = await import('xlsx-js-style')

  const translatedHeaders = headers.map(h => translateVehicleHeader(h, t))
  const dataRows = records.map(r => headers.map(h => formatValue(r[h], t) ?? ''))

  const ws = XLSX.utils.aoa_to_sheet([translatedHeaders, ...dataRows])

  ws['!cols'] = translatedHeaders.map(() => ({ wch: 18 }))

  translatedHeaders.forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: { rgb: 'F0F0F0' }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  dataRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex + 1,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Vehicles')

  XLSX.writeFile(wb, `vehicles_${fileStamp()}.xlsx`)
}

export const exportVehiclesPDF = async (records, headers, t) => {

  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')
  const autoTable = autoTableModule.default || autoTableModule.autoTable

  const translatedHeaders = headers.map(h => translateVehicleHeader(h, t))

  const dataRows = records.map(record =>
    headers.map(header => formatValue(record[header], t) ?? '')
  )

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureVehiclePdfFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const marginLeft = 8
  const marginRight = 8
  const usableWidth = pageWidth - marginLeft - marginRight
  const columnWidth = usableWidth / headers.length

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(7)
  doc.text(t('vehicle_list_title', { defaultValue: 'Zoznam vozidiel' }), marginLeft, 14)

  autoTable(doc, {
    head: [translatedHeaders],
    body: dataRows,
    startY: 18,
    theme: 'grid',
    margin: {
      top: 8,
      right: marginRight,
      bottom: 8,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 2.8,
      cellPadding: 0.7,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.15,
      lineColor: [0, 0, 0],
      minCellHeight: 6
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 2.8,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.2,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.15,
      lineColor: [0, 0, 0]
    },
    columnStyles: headers.reduce((acc, _header, index) => {
      acc[index] = {
        cellWidth: columnWidth
      }

      return acc
    }, {}),
    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `vehicles_${fileStamp()}.pdf`
  )
}

export const exportVehiclesXML = (records, headers, t) => {

  const rows = records
    .map(r => {
      const fields = headers
        .map(h => `<${h}>${formatValue(r[h], t)}</${h}>`)
        .join('')
      return `<vehicle>${fields}</vehicle>`
    })
    .join('')

  const xml = `<?xml version="1.0" encoding="UTF-8"?><vehicles>${rows}</vehicles>`

  downloadBlob(
    new Blob([xml], { type:'application/xml;charset=utf-8;' }),
    `vehicles_${fileStamp()}.xml`
  )
}

export const exportVehiclesJSON = (records) => {

  downloadBlob(
    new Blob(
      [JSON.stringify(records, null, 2)],
      { type:'application/json;charset=utf-8;' }
    ),
    `vehicles_${fileStamp()}.json`
  )
}

export const exportVehiclesICS = (records) => {

  const events = records
    .map(r => {

      const date =
        r?.startDate
          ? new Date(r.startDate)
              .toISOString()
              .replace(/[-:]/g,'')
              .split('.')[0]
          : ''

      return `BEGIN:VEVENT
SUMMARY:Vehicle ${r?.name ?? ''}
DTSTART:${date}
DESCRIPTION:${r?.licensePlate ?? ''}
END:VEVENT`
    })
    .join('\n')

  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Vehicles//EN
${events}
END:VCALENDAR`

  const blob = new Blob([ics], { type:'text/calendar;charset=utf-8;' })

  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'vehicles.ics'
  a.click()
}